import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Chevron } from 'components/Chevron';
import { GetTheAppButtons } from 'components/GetTheAppButtons/GetTheAppButtons';
import { getAnonymousId } from 'modules/meta/selectors';
import { Button } from '@daedalus/atlas/Button';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { PageWidthContainer } from '@daedalus/atlas/helpers/PageWidthContainer';
import { Icon, IconName } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import { trackLoginClick } from '@daedalus/core/src/auth/modules/actions';
import { TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY } from '@daedalus/core/src/auth/modules/actions';
import { selectIsAuthenticated } from '@daedalus/core/src/auth/modules/selectors';
import { AuthenticationFlows, SourceComponentType } from '@daedalus/core/src/auth/types/Auth';
import { Placement } from '@daedalus/core/src/deepLinking/types';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { useLogIn } from '@daedalus/shared/src/authentication/hooks/useLogIn';
import { HomePageSectionTitle } from '../commonStyles';
import { HighlightedTitle } from '../HighlightedTitle';
const SectionWrapper = /*#__PURE__*/styled('div')({
  name: "SectionWrapper",
  class: "sx7qle7",
  propsAsIs: false
});
const _exp4 = /*#__PURE__*/() => ContentWrapper;
const ComparisonWrapper = /*#__PURE__*/styled(_exp4())({
  name: "ComparisonWrapper",
  class: "c1y16do3",
  propsAsIs: true
});
const _exp9 = /*#__PURE__*/() => Icon;
const ComparisonItemIcon = /*#__PURE__*/styled(_exp9())({
  name: "ComparisonItemIcon",
  class: "ct4itpt",
  propsAsIs: true
});
const _exp13 = /*#__PURE__*/() => Text;
const ComparisonItemTitle = /*#__PURE__*/styled(_exp13())({
  name: "ComparisonItemTitle",
  class: "c1nutp69",
  propsAsIs: true
});
const _exp15 = /*#__PURE__*/() => ContentWrapper;
const GetAppWrapper = /*#__PURE__*/styled(_exp15())({
  name: "GetAppWrapper",
  class: "g1jxov59",
  propsAsIs: true
});
const comparisonTexts = defineMessages({
  exclusiveDealsTitle: {
    id: 'homePage.savingExclusiveDealsTitle',
    defaultMessage: 'We find exclusive deals'
  },
  exclusiveDealsSubtitle: {
    id: 'homePage.savingExclusiveDealsSubtitle',
    defaultMessage: 'with discounts up to 45%'
  },
  compareTravelSitesTitle: {
    id: 'homePage.savingCompareTravelSitesTitle',
    defaultMessage: 'And compare top travel sites'
  },
  compareTravelSitesSubtitle: {
    id: 'homePage.savingCompareTravelSitesSubtitle',
    defaultMessage: 'like Expedia and Agoda'
  },
  youBookTheBestTitle: {
    id: 'homePage.savingYouBookTheBestTitle',
    defaultMessage: 'Then you book the best'
  },
  youBookTheBestSubtitle: {
    id: 'homePage.savingYouBookTheBestSubtitle',
    defaultMessage: 'with us or another site'
  }
});
const comparisonItems = [{
  title: comparisonTexts.exclusiveDealsTitle,
  subtitle: comparisonTexts.exclusiveDealsSubtitle,
  iconName: 'FindBaggage'
}, {
  title: comparisonTexts.compareTravelSitesTitle,
  subtitle: comparisonTexts.compareTravelSitesSubtitle,
  iconName: 'Medal'
}, {
  title: comparisonTexts.youBookTheBestTitle,
  subtitle: comparisonTexts.youBookTheBestSubtitle,
  iconName: 'Podium'
}];
export const SavingSection = () => {
  const dispatch = useDispatch();
  const logIn = useLogIn();
  const {
    isMobile
  } = useDeviceLayout();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const anonymousId = useSelector(getAnonymousId);
  const {
    formatMessage
  } = useIntl();
  const handleSingInClick = () => {
    dispatch(trackLoginClick(SourceComponentType.SavingSectionHomePage));
    setTimeout(() => logIn({
      sourceComponent: SourceComponentType.SavingSectionHomePage,
      anonymousId,
      flow: AuthenticationFlows.SignUp
    }), TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY);
  };
  return <PageWidthContainer>
      <SectionWrapper>
        <ContentWrapper paddingTop={isMobile ? 's700' : 's950'} paddingBottom={isMobile ? 's900' : 's950'} paddingX={isMobile ? 's600' : 's900'}>
          <ContentWrapper paddingBottom={isMobile ? 's800' : 's900'}>
            <HomePageSectionTitle variant={isMobile ? 'titleXL' : 'title4XL'} className="__center">
              <HighlightedTitle source="MonthlyTravelSavings" />
            </HomePageSectionTitle>
          </ContentWrapper>

          <ComparisonWrapper>
            {comparisonItems.map((item, index) => <div key={index}>
                <ComparisonItemIcon name={item.iconName as IconName} size="xl" />
                <ComparisonItemTitle variant="titleL" align="center">
                  {formatMessage(item.title)}
                </ComparisonItemTitle>
                <Text variant="bodyM" align="center" colorPath="content.neutral.c700">
                  {formatMessage(item.subtitle)}
                </Text>
              </div>)}
          </ComparisonWrapper>
        </ContentWrapper>
        <GetAppWrapper paddingY={isMobile ? 's700' : 's900'} paddingX={isMobile ? 's600' : 's900'}>
          <Text variant={isMobile ? 'titleL' : 'title2XL'} align={isMobile ? 'center' : 'start'}>
            {isAuthenticated ? <FormattedMessageWrapper id="homePage.accessBestDealApp" defaultMessage="Access our best deals on our app" /> : <FormattedMessageWrapper id="homePage.getFreeAccessToDeals" defaultMessage="Get free access to deals you won’t find anywhere else" />}
          </Text>

          {isAuthenticated ? <GetTheAppButtons placement={Placement.HomePageSavingsSection} /> : <Button variant="primary" fullWidth={isMobile} size="xl" isRounded iconEnd={<Chevron size="md" />} onClick={handleSingInClick}>
              <FormattedMessageWrapper id="signInButton" defaultMessage="Sign in" />
            </Button>}
        </GetAppWrapper>
      </SectionWrapper>
    </PageWidthContainer>;
};

require("./SavingSection.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SavingSection.tsx");